import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { z } from "zod";
import { PlatformLayout } from "./layouts/PlatformLayout";

const LoginPage = lazy(() =>
  import("../routes/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const WorkbenchPage = lazy(() =>
  import("../routes/WorkbenchPage").then((module) => ({
    default: module.WorkbenchPage,
  })),
);
const RiskPage = lazy(() =>
  import("../routes/RiskPage").then((module) => ({ default: module.RiskPage })),
);
const HazardsPage = lazy(() =>
  import("../routes/HazardsPage").then((module) => ({
    default: module.HazardsPage,
  })),
);
const InspectionsPage = lazy(() =>
  import("../routes/InspectionsPage").then((module) => ({
    default: module.InspectionsPage,
  })),
);
const ReportsPage = lazy(() =>
  import("../routes/ReportsPage").then((module) => ({
    default: module.ReportsPage,
  })),
);
const ResponsibilitiesPage = lazy(() =>
  import("../routes/ResponsibilitiesPage").then((module) => ({
    default: module.ResponsibilitiesPage,
  })),
);
const InstitutionsPage = lazy(() =>
  import("../routes/InstitutionsPage").then((module) => ({
    default: module.InstitutionsPage,
  })),
);
const PerformancePage = lazy(() =>
  import("../routes/PerformancePage").then((module) => ({
    default: module.PerformancePage,
  })),
);
const MeetingsPage = lazy(() =>
  import("../routes/MeetingsPage").then((module) => ({
    default: module.MeetingsPage,
  })),
);
const EmergencyPage = lazy(() =>
  import("../routes/EmergencyPage").then((module) => ({
    default: module.EmergencyPage,
  })),
);
const AccidentsPage = lazy(() =>
  import("../routes/AccidentsPage").then((module) => ({
    default: module.AccidentsPage,
  })),
);
const MobileEmergencyPage = lazy(() =>
  import("../routes/MobileEmergencyPage").then((module) => ({
    default: module.MobileEmergencyPage,
  })),
);
const MobileAccidentsPage = lazy(() =>
  import("../routes/MobileAccidentsPage").then((module) => ({
    default: module.MobileAccidentsPage,
  })),
);
const TrainingPage = lazy(() =>
  import("../routes/TrainingPage").then((module) => ({
    default: module.TrainingPage,
  })),
);
const LicensesPage = lazy(() =>
  import("../routes/LicensesPage").then((module) => ({
    default: module.LicensesPage,
  })),
);
const ContractorsPage = lazy(() =>
  import("../routes/ContractorsPage").then((module) => ({
    default: module.ContractorsPage,
  })),
);
const InvestmentPage = lazy(() =>
  import("../routes/InvestmentPage").then((module) => ({
    default: module.InvestmentPage,
  })),
);
const MobileTrainingPage = lazy(() =>
  import("../routes/MobileTrainingPage").then((module) => ({
    default: module.MobileTrainingPage,
  })),
);
const MobileLicensesPage = lazy(() =>
  import("../routes/MobileLicensesPage").then((module) => ({
    default: module.MobileLicensesPage,
  })),
);
const MobileContractorsPage = lazy(() =>
  import("../routes/MobileContractorsPage").then((module) => ({
    default: module.MobileContractorsPage,
  })),
);
const MobileInvestmentPage = lazy(() =>
  import("../routes/MobileInvestmentPage").then((module) => ({
    default: module.MobileInvestmentPage,
  })),
);
const AnalyticsPage = lazy(() =>
  import("../routes/AnalyticsPage").then((module) => ({
    default: module.AnalyticsPage,
  })),
);
const ArchivesPage = lazy(() =>
  import("../routes/ArchivesPage").then((module) => ({
    default: module.ArchivesPage,
  })),
);
const OrganizationPage = lazy(() =>
  import("../routes/OrganizationPage").then((module) => ({
    default: module.OrganizationPage,
  })),
);
const ConfigurationPage = lazy(() =>
  import("../routes/ConfigurationPage").then((module) => ({
    default: module.ConfigurationPage,
  })),
);
const DashboardsPage = lazy(() =>
  import("../routes/DashboardsPage").then((module) => ({
    default: module.DashboardsPage,
  })),
);
const DashboardScreenPage = lazy(() =>
  import("../routes/DashboardsPage").then((module) => ({
    default: module.DashboardScreenPage,
  })),
);
const IntegrationsPage = lazy(() =>
  import("../routes/IntegrationsPage").then((module) => ({
    default: module.IntegrationsPage,
  })),
);
const SubmissionsPage = lazy(() =>
  import("../routes/SubmissionsPage").then((module) => ({
    default: module.SubmissionsPage,
  })),
);
const MobileWorkbenchPage = lazy(() =>
  import("../routes/MobileWorkbenchPage").then((module) => ({
    default: module.MobileWorkbenchPage,
  })),
);
const MobileResponsibilitiesPage = lazy(() =>
  import("../routes/MobileResponsibilitiesPage").then((module) => ({ default: module.MobileResponsibilitiesPage })),
);
const MobileInstitutionsPage = lazy(() =>
  import("../routes/MobileInstitutionsPage").then((module) => ({ default: module.MobileInstitutionsPage })),
);
const MobileRiskPage = lazy(() =>
  import("../routes/MobileRiskPage").then((module) => ({ default: module.MobileRiskPage })),
);
const MobileHazardsPage = lazy(() =>
  import("../routes/MobileHazardsPage").then((module) => ({ default: module.MobileHazardsPage })),
);
const MobileInspectionsPage = lazy(() =>
  import("../routes/MobileInspectionsPage").then((module) => ({ default: module.MobileInspectionsPage })),
);
const MobileReportsPage = lazy(() =>
  import("../routes/MobileReportsPage").then((module) => ({ default: module.MobileReportsPage })),
);
const MobileMeetingsPage = lazy(() =>
  import("../routes/MobileMeetingsPage").then((module) => ({ default: module.MobileMeetingsPage })),
);
const MobilePerformancePage = lazy(() =>
  import("../routes/MobilePerformancePage").then((module) => ({ default: module.MobilePerformancePage })),
);

const rootRoute = createRootRoute({
  component: () => (
    <Suspense
      fallback={<div className="route-loading">正在加载安全监管平台…</div>}
    >
      <Outlet />
    </Suspense>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <Navigate to="/login" replace />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "platform",
  component: PlatformLayout,
});

const workbenchSearch = z.object({
  view: z
    .enum([
      "overview",
      "todos",
      "messages",
      "duties",
      "licenses",
      "calendar",
      "shortcuts",
    ])
    .catch("overview"),
});

const riskSearch = z.object({
  view: z
    .enum([
      "list",
      "hazard",
      "assessment",
      "control",
      "notice",
      "dynamic",
      "stats",
      "alerts",
    ])
    .catch("list"),
});

const hazardSearch = z.object({
  view: z
    .enum(["list", "rectify", "accept", "major", "external", "stats"])
    .catch("list"),
});

const inspectionSearch = z.object({
  view: z
    .enum(["plan", "templates", "execution", "tasks", "notices", "ledger", "patrol"])
    .catch("plan"),
});

const reportSearch = z.object({
  view: z
    .enum(["submit", "verify", "reward", "supervise", "stats", "disclosure"])
    .catch("submit"),
});

const responsibilitySearch = z.object({
  view: z
    .enum([
      "targets",
      "tracking",
      "templates",
      "signing",
      "checklist",
      "positions",
      "duties",
      "reviews",
      "stats",
    ])
    .catch("targets"),
});

const institutionSearch = z.object({
  view: z
    .enum(["checklist", "regulations", "documents", "learning"])
    .catch("checklist"),
});

const performanceSearch = z.object({
  view: z.enum(["indicators", "implementation", "results"]).catch("indicators"),
});

const meetingSearch = z.object({
  view: z.enum(["ledger", "stats"]).catch("ledger"),
});

const emergencySearch = z.object({
  view: z
    .enum(["plans", "drills", "resources", "duty", "stats"])
    .catch("plans"),
});

const accidentSearch = z.object({
  view: z
    .enum(["ledger", "investigations", "stats", "roadnet"])
    .catch("ledger"),
});

const mobileEmergencySearch = z.object({
  view: z
    .enum(["plans", "resources", "duty", "notices", "drills"])
    .catch("plans"),
});

const mobileAccidentSearch = z.object({
  view: z.enum(["report", "list"]).catch("report"),
});

const trainingSearch = z.object({
  view: z
    .enum([
      "plans",
      "courses",
      "questions",
      "exams",
      "learning",
      "archives",
      "stats",
    ])
    .catch("plans"),
});

const licensesSearch = z.object({
  view: z.enum(["enterprise", "personnel", "warnings"]).catch("enterprise"),
});

const contractorsSearch = z.object({
  view: z
    .enum(["access", "agreements", "process", "performance"])
    .catch("access"),
});

const investmentSearch = z.object({
  view: z.enum(["plans", "adjustments", "expenses", "stats"]).catch("plans"),
});

const mobileTrainingSearch = z.object({
  view: z.enum(["records", "courses", "exams", "notices"]).catch("records"),
});
const mobileLicensesSearch = z.object({
  view: z.enum(["cards", "alerts"]).catch("cards"),
});
const mobileContractorsSearch = z.object({
  view: z.enum(["inspection", "search"]).catch("inspection"),
});
const mobileInvestmentSearch = z.object({
  view: z.enum(["expense", "records"]).catch("expense"),
});
const analyticsSearch = z.object({
  view: z.enum(["domains", "cross", "reports"]).catch("domains"),
});
const archivesSearch = z.object({
  view: z
    .enum(["ledger", "annual", "records", "diagnosis", "handover"])
    .catch("ledger"),
});
const organizationSearch = z.object({
  view: z
    .enum([
      "units",
      "departments",
      "committee",
      "positions",
      "people",
      "permissions",
      "parttime",
      "operations",
    ])
    .catch("units"),
});
const configurationSearch = z.object({
  view: z
    .enum([
      "dictionaries",
      "classifications",
      "codes",
      "workflows",
      "parameters",
    ])
    .catch("dictionaries"),
});
const dashboardSearch = z.object({
  view: z
    .enum([
      "overview",
      "business",
      "roadnet",
      "weather",
      "supervision",
      "management",
    ])
    .catch("overview"),
});
const integrationsSearch = z.object({
  view: z
    .enum(["comprehensive", "channels", "data", "monitor", "validation"])
    .catch("comprehensive"),
});
const submissionsSearch = z.object({
  view: z
    .enum([
      "monitor",
      "organization",
      "monthly",
      "hazards",
      "accidents",
      "hidden",
      "history",
    ])
    .catch("monitor"),
});
const mobileWorkbenchSearch = z.object({
  view: z
    .enum(["overview", "field", "learning", "profile", "todos", "messages", "calendar"])
    .catch("overview"),
});
const mobileResponsibilitiesSearch = z.object({ view: z.enum(["profile", "duties", "targets", "signing"]).catch("profile") });
const mobileInstitutionsSearch = z.object({ view: z.enum(["laws", "documents", "learning", "notices"]).catch("laws") });
const mobileRiskSearch = z.object({ view: z.enum(["list", "cards", "notices", "control"]).catch("list") });
const mobileHazardsSearch = z.object({ view: z.enum(["list", "tasks", "report"]).catch("list") });
const mobileInspectionsSearch = z.object({ view: z.enum(["tasks", "patrol"]).catch("tasks") });
const mobileReportsSearch = z.object({ view: z.enum(["submit", "records", "rewards"]).catch("submit") });
const mobileMeetingsSearch = z.object({ view: z.enum(["notices", "attendance"]).catch("notices") });
const mobilePerformanceSearch = z.object({ view: z.enum(["results", "detail"]).catch("results") });

export const workbenchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/workbench",
  validateSearch: (search) => workbenchSearch.parse(search),
  component: WorkbenchPage,
});

export const riskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/risk",
  validateSearch: (search) => riskSearch.parse(search),
  component: RiskPage,
});

export const hazardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hazards",
  validateSearch: (search) => hazardSearch.parse(search),
  component: HazardsPage,
});

export const inspectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inspections",
  validateSearch: (search) => inspectionSearch.parse(search),
  component: InspectionsPage,
});

export const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  validateSearch: (search) => reportSearch.parse(search),
  component: ReportsPage,
});

export const responsibilitiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/responsibilities",
  validateSearch: (search) => responsibilitySearch.parse(search),
  component: ResponsibilitiesPage,
});

export const institutionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/institutions",
  validateSearch: (search) => institutionSearch.parse(search),
  component: InstitutionsPage,
});

export const performanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance",
  validateSearch: (search) => performanceSearch.parse(search),
  component: PerformancePage,
});

export const meetingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/meetings",
  validateSearch: (search) => meetingSearch.parse(search),
  component: MeetingsPage,
});

export const emergencyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/emergency",
  validateSearch: (search) => emergencySearch.parse(search),
  component: EmergencyPage,
});

export const accidentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accidents",
  validateSearch: (search) => accidentSearch.parse(search),
  component: AccidentsPage,
});

export const trainingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/training",
  validateSearch: (search) => trainingSearch.parse(search),
  component: TrainingPage,
});

export const licensesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/licenses",
  validateSearch: (search) => licensesSearch.parse(search),
  component: LicensesPage,
});

export const contractorsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contractors",
  validateSearch: (search) => contractorsSearch.parse(search),
  component: ContractorsPage,
});

export const investmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/investment",
  validateSearch: (search) => investmentSearch.parse(search),
  component: InvestmentPage,
});

export const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/analytics",
  validateSearch: (search) => analyticsSearch.parse(search),
  component: AnalyticsPage,
});

export const archivesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/archives",
  validateSearch: (search) => archivesSearch.parse(search),
  component: ArchivesPage,
});

export const organizationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization",
  validateSearch: (search) => organizationSearch.parse(search),
  component: OrganizationPage,
});

export const configurationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/configuration",
  validateSearch: (search) => configurationSearch.parse(search),
  component: ConfigurationPage,
});

export const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboards",
  validateSearch: (search) => dashboardSearch.parse(search),
  component: DashboardsPage,
});

export const integrationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/integrations",
  validateSearch: (search) => integrationsSearch.parse(search),
  component: IntegrationsPage,
});

export const submissionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/submissions",
  validateSearch: (search) => submissionsSearch.parse(search),
  component: SubmissionsPage,
});

export const dashboardScreenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bigscreen",
  component: DashboardScreenPage,
});

export const mobileWorkbenchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mobile/workbench",
  validateSearch: (search) => mobileWorkbenchSearch.parse(search),
  component: MobileWorkbenchPage,
});

export const mobileResponsibilitiesRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mobile/responsibilities", validateSearch: (search) => mobileResponsibilitiesSearch.parse(search), component: MobileResponsibilitiesPage });
export const mobileInstitutionsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mobile/institutions", validateSearch: (search) => mobileInstitutionsSearch.parse(search), component: MobileInstitutionsPage });
export const mobileRiskRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mobile/risk", validateSearch: (search) => mobileRiskSearch.parse(search), component: MobileRiskPage });
export const mobileHazardsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mobile/hazards", validateSearch: (search) => mobileHazardsSearch.parse(search), component: MobileHazardsPage });
export const mobileInspectionsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mobile/inspections", validateSearch: (search) => mobileInspectionsSearch.parse(search), component: MobileInspectionsPage });
export const mobileReportsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mobile/reports", validateSearch: (search) => mobileReportsSearch.parse(search), component: MobileReportsPage });
export const mobileMeetingsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mobile/meetings", validateSearch: (search) => mobileMeetingsSearch.parse(search), component: MobileMeetingsPage });
export const mobilePerformanceRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mobile/performance", validateSearch: (search) => mobilePerformanceSearch.parse(search), component: MobilePerformancePage });

export const mobileEmergencyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mobile/emergency",
  validateSearch: (search) => mobileEmergencySearch.parse(search),
  component: MobileEmergencyPage,
});

export const mobileAccidentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mobile/accidents",
  validateSearch: (search) => mobileAccidentSearch.parse(search),
  component: MobileAccidentsPage,
});

export const mobileTrainingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mobile/training",
  validateSearch: (search) => mobileTrainingSearch.parse(search),
  component: MobileTrainingPage,
});

export const mobileLicensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mobile/licenses",
  validateSearch: (search) => mobileLicensesSearch.parse(search),
  component: MobileLicensesPage,
});

export const mobileContractorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mobile/contractors",
  validateSearch: (search) => mobileContractorsSearch.parse(search),
  component: MobileContractorsPage,
});

export const mobileInvestmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mobile/investment",
  validateSearch: (search) => mobileInvestmentSearch.parse(search),
  component: MobileInvestmentPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  mobileEmergencyRoute,
  mobileAccidentsRoute,
  mobileTrainingRoute,
  mobileLicensesRoute,
  mobileContractorsRoute,
  mobileInvestmentRoute,
  mobileResponsibilitiesRoute,
  mobileInstitutionsRoute,
  mobileRiskRoute,
  mobileHazardsRoute,
  mobileInspectionsRoute,
  mobileReportsRoute,
  mobileMeetingsRoute,
  mobilePerformanceRoute,
  mobileWorkbenchRoute,
  dashboardScreenRoute,
  appRoute.addChildren([
    workbenchRoute,
    riskRoute,
    hazardsRoute,
    inspectionsRoute,
    reportsRoute,
    responsibilitiesRoute,
    institutionsRoute,
    performanceRoute,
    meetingsRoute,
    emergencyRoute,
    accidentsRoute,
    trainingRoute,
    licensesRoute,
    contractorsRoute,
    investmentRoute,
    analyticsRoute,
    archivesRoute,
    organizationRoute,
    configurationRoute,
    dashboardRoute,
    integrationsRoute,
    submissionsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
